import { formatDisplayEmail } from '@bs/core'
import { Button, pantryColors } from '@pantry/ui'
import React, { useState } from 'react'
import { useDebounce } from '../../../../hooks/useDebounce'
import type { CartRow, CartTimelineEvent } from '../../../../types/cartRecovery'
import type {
  LoyaltyFlowCart,
  LoyaltyFlowCustomerDetail,
  LoyaltyFlowCustomer,
  LoyaltyFlowCustomerIdentity,
} from '../../../../types/loyaltyMarketing'
import { ProductThumb } from '../../../Customer/components/profile/tabs/behavior/v2/panels/products/ProductThumb'
import {
  useLoyaltyFlowCustomerDetailQuery,
  useLoyaltyFlowCustomersQuery,
} from '../../hooks/useLoyaltyMarketing'
import {
  CartActions,
  CartTimeline,
  CustomerCard,
  LatestEmailCart,
  OutcomeBox,
  SectionTitle,
  type TimelineRow,
  UpNextList,
} from '../cartRecovery/CartRecord'
import { useCartRecoveryCartQuery } from '../cartRecovery/useCartRecovery'
import { useMarketingToast } from '../hooks/useMarketingToast'
import { ActivityMessage } from './CampaignActivityList'
import {
  STATUS_TEXT,
  eventLabel,
  formatWhen,
  reasonText,
  statusColor,
} from './FlowActivityList'

const p = pantryColors

const customerLabel = (c: {
  name?: string
  email: string
  customer_id: string
}): string => c.name || formatDisplayEmail(c.email) || c.customer_id

const CART_STATUS: Record<string, string> = {
  active: 'Still in cart',
  converted: 'Order placed',
  expired: 'Cart expired',
}

const UnsubscribedBadge: React.FC = () => (
  <span
    style={{
      fontSize: 10.5,
      fontWeight: 600,
      padding: '1px 7px',
      borderRadius: 10,
      background: p.red[50],
      color: p.red[700],
      whiteSpace: 'nowrap',
    }}
  >
    Unsubscribed
  </span>
)

const CustomerRow: React.FC<{
  customer: LoyaltyFlowCustomer
  onOpen: () => void
}> = ({ customer, onOpen }) => {
  const label = customerLabel(customer)
  const email = formatDisplayEmail(customer.email)
  const counts = [
    `${customer.sent} sent`,
    ...(customer.skipped ? [`${customer.skipped} skipped`] : []),
    ...(customer.failed ? [`${customer.failed} failed`] : []),
    `${customer.opens ?? 0} opens`,
    `${customer.clicks ?? 0} clicks`,
  ].join(' · ')
  return (
    <button
      type="button"
      onClick={onOpen}
      style={{
        display: 'flex',
        width: '100%',
        gap: 12,
        padding: '11px 0',
        border: 'none',
        borderBottom: `1px solid ${p.gray[100]}`,
        background: 'transparent',
        cursor: 'pointer',
        textAlign: 'left',
      }}
    >
      <span
        style={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          flexShrink: 0,
          marginTop: 5,
          background: statusColor(customer.last_status),
        }}
      />
      <span style={{ flex: 1, minWidth: 0 }}>
        <span
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            flexWrap: 'wrap',
            fontSize: 13,
            fontWeight: 600,
            color: p.gray[800],
          }}
        >
          {label}
          {!customer.subscribed && customer.email && <UnsubscribedBadge />}
        </span>
        {email && email !== label && (
          <span style={{ display: 'block', fontSize: 12, color: p.gray[500] }}>
            {email}
          </span>
        )}
        <span
          style={{
            display: 'block',
            fontSize: 12,
            color: p.gray[500],
            marginTop: 2,
          }}
        >
          {counts}
        </span>
      </span>
      <span style={{ textAlign: 'right', flexShrink: 0 }}>
        <span style={{ display: 'block', fontSize: 12, color: p.gray[700] }}>
          {STATUS_TEXT[customer.last_status] ?? customer.last_status}
        </span>
        <span style={{ display: 'block', fontSize: 11.5, color: p.gray[400] }}>
          {formatWhen(customer.last_at)}
        </span>
      </span>
    </button>
  )
}

const CartDetail: React.FC<{ cart: LoyaltyFlowCart | null }> = ({ cart }) => {
  if (!cart)
    return (
      <div style={{ fontSize: 13, color: p.gray[500], padding: '8px 0' }}>
        No cart found for this customer.
      </div>
    )
  return (
    <div>
      {cart.items.length === 0 ? (
        <div style={{ fontSize: 13, color: p.gray[500], padding: '8px 0' }}>
          This cart has no items.
        </div>
      ) : (
        cart.items.map((item, i) => (
          <div
            key={`${item.item_name}-${item.item_variant}-${i}`}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: '9px 0',
              borderBottom: `1px solid ${p.gray[100]}`,
            }}
          >
            <ProductThumb
              src={item.item_image}
              name={item.item_name}
              size={38}
            />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{ fontSize: 13, fontWeight: 600, color: p.gray[800] }}
              >
                {item.item_name}
              </div>
              <div style={{ fontSize: 12, color: p.gray[500] }}>
                {[item.item_variant, `Qty ${item.item_qty}`]
                  .filter(Boolean)
                  .join(' · ')}
              </div>
            </div>
            <div style={{ fontSize: 13, color: p.gray[700] }}>
              {item.item_price}
            </div>
          </div>
        ))
      )}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          padding: '10px 0 0',
          fontSize: 13,
        }}
      >
        <span style={{ color: p.gray[500] }}>
          {CART_STATUS[cart.status] ?? cart.status}
        </span>
        <span style={{ fontWeight: 700, color: p.gray[800] }}>
          Cart total: {cart.total || '—'}
        </span>
      </div>
    </div>
  )
}

const SummaryStats: React.FC<{
  summary: NonNullable<LoyaltyFlowCustomerDetail['summary']>
}> = ({ summary }) => (
  <div
    style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(4, 1fr)',
      margin: '12px 0 0',
      border: `1px solid ${p.gray[200]}`,
      borderRadius: 10,
    }}
  >
    {(
      [
        ['Sent', summary.sent],
        ['Skipped', summary.skipped],
        ['Opens', summary.opens],
        ['Clicks', summary.clicks],
      ] as const
    ).map(([label, value]) => (
      <div key={label} style={{ padding: '10px 12px', textAlign: 'center' }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: p.gray[800] }}>
          {value}
        </div>
        <div style={{ fontSize: 11.5, color: p.gray[500] }}>{label}</div>
      </div>
    ))}
  </div>
)

// Every send, open, click and skip for this customer, newest first. `run` adds
// the cart's own start and order; logged rows cover what no record holds.
export const customerTimelineRows = (
  detail: LoyaltyFlowCustomerDetail,
  cartTimeline?: CartTimelineEvent[],
): TimelineRow[] => {
  const rows: TimelineRow[] = []
  const run: CartRow | null | undefined = detail.run
  for (const email of detail.emails ?? []) {
    const id = `${email.run_id ?? 'log'}-${email.number}-${email.sent_at}`
    rows.push({
      key: `sent-${id}`,
      kind: 'sent',
      at: email.sent_at,
      text: `Email ${email.number} sent${email.manual ? ' manually' : ''}`,
      sub: email.subject,
      email,
    })
    if (email.opened_at)
      rows.push({
        key: `open-${id}`,
        kind: 'open',
        at: email.opened_at,
        text: `Opened Email ${email.number}`,
        sub: email.opens > 1 ? `${email.opens} times` : undefined,
      })
    if (email.clicked_at)
      rows.push({
        key: `click-${id}`,
        kind: 'click',
        at: email.clicked_at,
        text: `Clicked a link in Email ${email.number}`,
        sub: email.links[0],
      })
  }
  for (const event of detail.events) {
    // Sends with no captured record come back in `emails`; only fall back to
    // the log for a response that predates that field.
    if (event.status === 'sent' && detail.emails) continue
    rows.push({
      key: `event-${event.id}`,
      kind: event.status === 'sent' ? 'sent' : event.status,
      at: event.created_at,
      text: eventLabel(event),
      sub: reasonText(event.reason),
    })
  }
  if (run?.abandoned_at)
    rows.push({
      key: `abandoned-${run.id}`,
      kind: 'abandoned',
      at: run.abandoned_at,
      text: 'Cart abandoned',
    })
  // The latest cart's ending, which no email or log row holds.
  for (const event of cartTimeline ?? []) {
    if (event.type !== 'stopped' && event.type !== 'lost') continue
    rows.push({
      key: `${event.type}-${run?.id}`,
      kind: event.type,
      at: event.at,
      text: event.text,
      sub: event.sub,
    })
  }
  if (run?.order)
    rows.push({
      key: `order-${run.id}`,
      kind: 'order',
      at: run.order.at,
      text: run.order.number
        ? `Placed order #${run.order.number}`
        : 'Placed order',
    })
  return rows.sort(
    (a, b) => new Date(b.at ?? 0).getTime() - new Date(a.at ?? 0).getTime(),
  )
}

const CustomerDetail: React.FC<{
  storeId: string
  flowId: string
  customer: LoyaltyFlowCustomerIdentity
  cartFlow: boolean
  onBack: () => void
}> = ({ storeId, flowId, customer, cartFlow, onBack }) => {
  const { data, isLoading, isError } = useLoyaltyFlowCustomerDetailQuery(
    storeId,
    flowId,
    customer,
  )
  // The latest cart's next emails and actions.
  const { data: cart } = useCartRecoveryCartQuery(storeId, data?.run?.id)
  const { showToast, toastNode } = useMarketingToast()
  const emails = data?.emails ?? []
  return (
    <div>
      <Button variant="ghost" size="sm" onClick={onBack}>
        ← All customers
      </Button>
      {isLoading && (
        <ActivityMessage loading>Loading customer…</ActivityMessage>
      )}
      {isError && (
        <ActivityMessage>
          Couldn't load this customer. Please try again.
        </ActivityMessage>
      )}
      {data && (
        <div style={{ marginTop: 10 }}>
          {data.run && <OutcomeBox cart={data.run} emails={emails} />}
          {data.summary && <SummaryStats summary={data.summary} />}
          <CustomerCard customer={data.customer} />
          {cartFlow && (
            <LatestEmailCart
              email={data.latest_email}
              order={data.run?.order}
              fallback={
                data.latest_email ? (
                  data.cart && (
                    <>
                      <SectionTitle>Current cart</SectionTitle>
                      <CartDetail cart={data.cart} />
                    </>
                  )
                ) : (
                  <CartDetail cart={data.cart} />
                )
              }
            />
          )}
          {cart && <UpNextList emails={cart.up_next} />}
          <SectionTitle right="Newest first">Activity</SectionTitle>
          <CartTimeline
            rows={customerTimelineRows(data, cart?.timeline).map((row) =>
              cartFlow ? row : { ...row, email: undefined },
            )}
            latestEmail={data.latest_email ? emails[0] : undefined}
          />
          {cart && (
            <div
              style={{
                marginTop: 6,
                paddingTop: 14,
                borderTop: `1px solid ${p.gray[100]}`,
              }}
            >
              <CartActions
                storeId={storeId}
                cart={cart}
                showToast={showToast}
              />
            </div>
          )}
        </div>
      )}
      {toastNode}
    </div>
  )
}

// Activity › Customer view: the customers this automation acted on, with
// search; clicking one shows their activity and the cart in each email sent.
export const FlowCustomerView: React.FC<{
  storeId: string
  flowId: string
  cartFlow?: boolean
  enabled?: boolean
}> = ({ storeId, flowId, cartFlow = true, enabled = true }) => {
  const [search, setSearch] = useState('')
  const debounced = useDebounce(search.trim(), 300)
  const [selected, setSelected] = useState<LoyaltyFlowCustomerIdentity | null>(
    null,
  )
  const {
    data,
    isLoading,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useLoyaltyFlowCustomersQuery(storeId, flowId, debounced, {
    enabled: enabled && !selected,
  })
  const customers = (data?.pages ?? []).flatMap((page) => page.customers)

  if (selected)
    return (
      <CustomerDetail
        cartFlow={cartFlow}
        storeId={storeId}
        flowId={flowId}
        customer={selected}
        onBack={() => setSelected(null)}
      />
    )

  return (
    <div>
      <input
        type="search"
        aria-label="Search customers"
        placeholder="Search by name, email or customer ID"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        style={{
          width: '100%',
          boxSizing: 'border-box',
          padding: '8px 12px',
          marginBottom: 4,
          border: `1px solid ${p.gray[200]}`,
          borderRadius: 6,
          fontSize: 13,
          outline: 'none',
        }}
      />
      {isLoading && (
        <ActivityMessage loading>Loading customers…</ActivityMessage>
      )}
      {isError && (
        <ActivityMessage>
          Couldn't load customers. Please try again.
        </ActivityMessage>
      )}
      {!isLoading && !isError && customers.length === 0 && (
        <ActivityMessage>
          {debounced ? 'No customers match your search.' : 'No customers yet.'}
        </ActivityMessage>
      )}
      {customers.map((customer) => (
        <CustomerRow
          key={`${customer.customer_id}|${customer.email}`}
          customer={customer}
          onOpen={() =>
            setSelected({
              customer_id: customer.customer_id,
              email: customer.email,
            })
          }
        />
      ))}
      {hasNextPage && (
        <div
          style={{ display: 'flex', justifyContent: 'center', marginTop: 12 }}
        >
          <Button
            variant="ghost"
            size="sm"
            disabled={isFetchingNextPage}
            onClick={() => fetchNextPage()}
          >
            {isFetchingNextPage ? 'Loading…' : 'Load more'}
          </Button>
        </div>
      )}
    </div>
  )
}
